/*
 **	Modern Device Card Component
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import React from 'react'
import { OCAColors, Typography, Spacing, Animation } from '../../theme/colors'

interface DeviceCardProps {
  icon?: React.ReactNode
  title: string
  subtitle?: string | React.ReactNode
  value?: string
  status?: string
  statusColor?: string
  onClick?: () => void
  isNew?: boolean
}

export const DeviceCard: React.FC<DeviceCardProps> = ({
  icon,
  title,
  subtitle,
  value,
  status,
  statusColor,
  onClick,
  isNew = false,
}) => {
  const isClickable = !!onClick
  const [isPressed, setIsPressed] = React.useState(false)
  const isAlarm = status === 'Alarm' || status === 'Alarm\n(Offline)'
  const isWarning = status === 'Warning'
  const isOffline = status === 'Offline' || status === 'No Data'

  // Determine card background based on status
  const getCardBackground = () => {
    if (isNew) {
      return 'linear-gradient(135deg, rgba(0, 122, 255, 0.08) 0%, rgba(0, 122, 255, 0.04) 100%)'
    }
    if (isAlarm) {
      return 'linear-gradient(135deg, rgba(255, 59, 48, 0.08) 0%, rgba(255, 59, 48, 0.04) 100%)'
    }
    if (isWarning) {
      return 'linear-gradient(135deg, rgba(255, 149, 0, 0.08) 0%, rgba(255, 149, 0, 0.04) 100%)'
    }
    return 'rgba(255, 255, 255, 0.98)'
  }

  // Determine border color based on status
  const getBorderColor = () => {
    if (isNew) return 'rgba(0, 122, 255, 0.2)'
    if (isAlarm) return 'rgba(255, 59, 48, 0.2)'
    if (isWarning) return 'rgba(255, 149, 0, 0.2)'
    return 'rgba(0, 0, 0, 0.06)'
  }

  return (
    <div
      onClick={onClick}
      style={{
        background: getCardBackground(),
        border: `1px solid ${getBorderColor()}`,
        borderRadius: '16px',
        padding: `${Spacing.md} ${Spacing.lg}`,
        marginBottom: Spacing.md,
        cursor: isClickable ? 'pointer' : 'default',
        boxShadow: isPressed
          ? '0 2px 8px rgba(0, 0, 0, 0.08)'
          : '0 4px 12px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)',
        transform: isPressed ? 'scale(0.98)' : 'scale(1)',
        transition: `all ${Animation.normal} cubic-bezier(0.4, 0, 0.2, 1)`,
        position: 'relative',
        overflow: 'hidden',
      }}
      onTouchStart={() => {
        if (isClickable) {
          setIsPressed(true)
        }
      }}
      onTouchEnd={() => {
        if (isClickable) {
          setIsPressed(false)
        }
      }}
      onTouchCancel={() => {
        if (isClickable) {
          setIsPressed(false)
        }
      }}
      onMouseDown={() => {
        if (isClickable) {
          setIsPressed(true)
        }
      }}
      onMouseUp={() => {
        if (isClickable) {
          setIsPressed(false)
        }
      }}
      onMouseLeave={() => {
        if (isClickable) {
          setIsPressed(false)
        }
      }}
    >
      {/* Status accent bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          background: statusColor || 'transparent',
          borderRadius: '16px 0 0 16px',
          opacity: 0.6,
        }}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          minHeight: '48px',
        }}
      >
        {/* Icon */}
        {icon && (
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: Spacing.md,
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              transition: `all ${Animation.normal} ${Animation.spring}`,
              transform: isPressed ? 'scale(0.95)' : 'scale(1)',
              alignSelf: 'flex-start',
            }}
          >
            {icon}
          </div>
        )}

        {/* Content */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            alignSelf: 'stretch',
            paddingRight: '90px',
          }}
        >
          <div
            style={{
              fontSize: Typography.fontSize.lg,
              fontWeight: Typography.fontWeight.semibold,
              color: OCAColors.label,
              lineHeight: '22px',
              marginBottom: subtitle ? '4px' : 0,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: Typography.fontSize.sm,
                color: OCAColors.secondaryLabel,
                lineHeight: '18px',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Value and Status Badge - Positioned absolutely at bottom-right */}
      <div
        style={{
          position: 'absolute',
          bottom: Spacing.md,
          right: Spacing.lg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '6px',
        }}
      >
        {value && (
          <div
            style={{
              fontSize: Typography.fontSize.sm,
              fontWeight: Typography.fontWeight.medium,
              color: OCAColors.secondaryLabel,
              textAlign: 'right',
            }}
          >
            {value}
          </div>
        )}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {isNew && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '4px 10px',
                borderRadius: '12px',
                backgroundColor: '#000000',
                border: '1px solid #000000',
                minHeight: '24px',
              }}
            >
              <span
                style={{
                  fontSize: Typography.fontSize.xs,
                  fontWeight: Typography.fontWeight.semibold,
                  color: '#FFFFFF',
                  letterSpacing: '0.2px',
                  lineHeight: '16px',
                }}
              >
                New
              </span>
            </div>
          )}
          {status && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '6px',
              padding: '4px 10px',
              borderRadius: '12px',
              minHeight: '24px',
              backgroundColor: isAlarm
                ? 'rgba(255, 59, 48, 0.12)'
                : isWarning
                ? 'rgba(255, 149, 0, 0.12)'
                : isOffline
                ? 'rgba(142, 142, 147, 0.12)'
                : 'rgba(52, 199, 89, 0.12)',
              border: `1px solid ${
                isAlarm
                  ? 'rgba(255, 59, 48, 0.2)'
                  : isWarning
                  ? 'rgba(255, 149, 0, 0.2)'
                  : isOffline
                  ? 'rgba(142, 142, 147, 0.2)'
                  : 'rgba(52, 199, 89, 0.2)'
              }`,
            }}
          >
            {/* Animated status dot for alarms */}
            {isAlarm && (
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: statusColor || OCAColors.danger,
                  display: 'inline-block',
                  animation: 'pulse 2s ease-in-out infinite',
                  marginTop: '5px',
                }}
              />
            )}
            <span
              style={{
                fontSize: Typography.fontSize.xs,
                fontWeight: Typography.fontWeight.semibold,
                color: statusColor || OCAColors.label,
                letterSpacing: '0.2px',
                whiteSpace: 'pre-line',
                textAlign: 'center',
                lineHeight: '16px',
              }}
            >
              {status}
            </span>
          </div>
          )}
        </div>
      </div>

      {/* Pulse animation for alarms */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.3);
          }
        }
      `}</style>
    </div>
  )
}
